import { useLocation } from '@solidjs/router'
import type { ParentProps } from 'solid-js'
import { type Component, createMemo, createSignal } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Drawer from '~/components/Drawer'
import Icon from '~/components/Icon'
import SidebarMenu from '~/components/SidebarMenu'
import Navbar from '~/layers/Navbar'
import type { SidebarMenuSectionType } from '~/types/SidebarMenu'

// Icon map for sidebar items
const iconMap: Record<string, string> = {
  Accordion: 'material-symbols-light:expand-more',
  AccordionGroup: 'material-symbols-light:expand-more',
  Alert: 'material-symbols-light:warning-outline',
  Avatar: 'material-symbols-light:account-circle-outline',
  Badge: 'material-symbols-light:badge-outline',
  Breadcrumbs: 'material-symbols-light:password',
  Button: 'material-symbols-light:open-in-new',
  ButtonGroup: 'material-symbols-light:open-in-new',
  Card: 'material-symbols-light:credit-card-outline',
  Checkbox: 'material-symbols-light:check-box-outline',
  Chip: 'material-symbols-light:label-outline',
  ColorPicker: 'material-symbols-light:palette-outline',
  Container: 'material-symbols-light:inventory-2-outline',
  DateInput: 'material-symbols-light:calendar-month-outline',
  Drawer: 'material-symbols-light:left-panel-open-outline',
  DrawerItem: 'material-symbols-light:more-horiz',
  EditorInput: 'material-symbols-light:edit-outline',
  Footer: 'material-symbols-light:view-carousel-outline',
  FileInput: 'material-symbols-light:upload-file-outline',
  Form: 'material-symbols-light:description-outline',
  Gallery: 'material-symbols-light:photo-library-outline',
  GoBack: 'material-symbols-light:arrow-back',
  Map: 'material-symbols-light:map-outline',
  Icon: 'material-symbols-light:star-outline',
  ImageViewer: 'material-symbols-light:image-outline',
  Input: 'material-symbols-light:text-fields',
  List: 'material-symbols-light:list',
  Menu: 'material-symbols-light:menu',
  MaskInput: 'material-symbols-light:attach-money',
  Modal: 'material-symbols-light:web',
  Radio: 'material-symbols-light:radio-button-checked-outline',
  Rating: 'material-symbols-light:star-outline',
  Select: 'material-symbols-light:sort',
  SelectModal: 'material-symbols-light:filter-list',
  SidebarMenu: 'material-symbols-light:left-panel-open-outline',
  Skeleton: 'material-symbols-light:grid-on-outline',
  Slider: 'material-symbols-light:tune',
  SwipeAction: 'material-symbols-light:tune',
  Switch: 'material-symbols-light:widgets-outline',
  Table: 'material-symbols-light:movie-outline',
  Tabs: 'material-symbols-light:folder-outline',
  TagInput: 'material-symbols-light:tag',
  Tags: 'material-symbols-light:tag',
  Textarea: 'material-symbols-light:text-fields',
  Tooltip: 'material-symbols-light:info-outline',
  UpdateBanner: 'material-symbols-light:refresh',
  useAlert: 'material-symbols-light:notifications-outline',
  useToast: 'material-symbols-light:widgets-outline'
} as const

// Lista generada de componentes en `src/components` y su página de demo esperada
export const componentsIndex = (() => {
  const names = [
    'Accordion',
    'AccordionGroup',
    'Alert',
    'Avatar',
    'Badge',
    'Breadcrumbs',
    'Button',
    'ButtonGroup',
    'Card',
    'Checkbox',
    'Chip',
    'ColorPicker',
    'Container',
    'DateInput',
    'Drawer',
    'DrawerItem',
    'EditorInput',
    'Footer',
    'FileInput',
    'Form',
    'Gallery',
    'GoBack',
    'Icon',
    'Map',
    'ImageViewer',
    'Input',
    'List',
    'Menu',
    'MaskInput',
    'Modal',
    'Radio',
    'Rating',
    'Select',
    'SelectModal',
    'SidebarMenu',
    'Skeleton',
    'Slider',
    'SwipeAction',
    'Switch',
    'Table',
    'Tabs',
    'TagInput',
    'Tags',
    'Textarea',
    'Tooltip',
    'UpdateBanner',
    'useAlert',
    'useToast'
  ]

  const slug = (n: string) => n.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

  return names.map(name => ({
    name,
    component: `components/${name}.tsx`,
    page: `pages/componentes/${slug(name)}.tsx`,
    pageExport: `${name}Page`,
    route: `componentes/${slug(name)}`,
    icon: iconMap[name] || 'material-symbols-light:description-outline'
  }))
})()

// Build sidebar sections from components index
const buildSections = (): SidebarMenuSectionType[] => {
  const componentItems = componentsIndex.map(c => ({
    key: c.name,
    label: c.name,
    icon: c.icon,
    href: `/${c.route}`
  }))

  return [
    {
      title: 'Navegación',
      items: [
        {
          key: 'home',
          label: 'Resumen',
          icon: 'material-symbols-light:home-outline',
          href: '/componentes'
        }
      ]
    },
    {
      title: 'Biblioteca de Componentes',
      items: componentItems
    },
    {
      title: 'Fundamentos',
      items: [
        {
          key: 'colors',
          label: 'Colores',
          icon: 'material-symbols-light:palette-outline',
          href: '/foundation/colors'
        },
        {
          key: 'typography',
          label: 'Tipografía',
          icon: 'material-symbols-light:font-download-outline',
          href: '/foundation/typography'
        }
      ]
    }
  ]
}

const sections = buildSections()

const ComponentLayout: Component<ParentProps> = props => {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = createSignal(false)
  const [sidebarCollapsed, setSidebarCollapsed] = createSignal(false)

  const openMobileMenu = () => setMobileOpen(true)
  const closeMobileMenu = () => setMobileOpen(false)

  const isActive = (href: string) => {
    if (href === '/componentes') return location.pathname === '/componentes'
    // Coincidencia por segmento: /componentes/button NO debe activarse en /componentes/button-group.
    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  const sidebarHeader = createMemo(() => (
    <div class={twMerge('flex items-center gap-2 transition-all duration-300', sidebarCollapsed() ? 'opacity-0 invisible w-0' : 'opacity-100 visible')}>
      <div class="p-1.5 rounded-lg bg-primary-500 text-white shadow-lg shadow-primary-500/20">
        <Icon icon="material-symbols-light:widgets-outline" size={20} />
      </div>
      <div class="flex flex-col min-w-0">
        <span class="text-[10px] font-black uppercase tracking-widest text-foreground truncate">UI LIBRARY</span>
        <span class="text-[9px] font-bold text-primary-500 tracking-tighter uppercase">{__APP_VERSION__}</span>
      </div>
    </div>
  ))

  return (
    <div class="min-h-screen flex bg-background text-foreground transition-colors duration-300">
      {/* Desktop sidebar — Full height integrated sidebar */}
      <aside class={twMerge('hidden lg:flex shrink-0 sticky top-0 h-screen transition-all duration-300 ease-in-out z-40', sidebarCollapsed() ? 'w-20' : 'w-72')}>
        <SidebarMenu
          sections={sections}
          collapsed={sidebarCollapsed()}
          onToggleCollapse={() => setSidebarCollapsed(prev => !prev)}
          isActive={isActive}
          header={sidebarHeader()}
          class="border-e border-border shadow-none bg-surface"
        />
      </aside>

      <div class={twMerge('flex-1 flex flex-col min-w-0 relative transition-all duration-300')}>
        <Navbar onComponentsMenuClick={openMobileMenu} showBreadcrumb />

        {/* Mobile drawer */}
        <Drawer isOpen={mobileOpen()} onClose={closeMobileMenu} position="left" size="sm" class="lg:hidden">
          <SidebarMenu
            sections={sections}
            collapsed={false}
            isActive={isActive}
            onNavigate={closeMobileMenu}
            header={
              <div class="flex items-center gap-3 p-1">
                <div class="p-2 rounded-xl bg-primary-500 text-white shadow-lg shadow-primary-500/30">
                  <Icon icon="material-symbols-light:widgets-outline" size={24} />
                </div>
                <div class="flex flex-col">
                  <span class="text-lg font-black text-foreground tracking-tighter">UI LIBRARY</span>
                  <span class="text-xs text-foreground-subtle font-medium tracking-widest uppercase">Florez Jaramillo</span>
                </div>
              </div>
            }
          />
        </Drawer>

        {/* Main content — Better scrolling and integrated feel */}
        <main class="flex-1 min-w-0 w-full">
          <div class="w-full transition-all duration-500">
            <div
              class={twMerge(
                'prose max-w-none',
                'prose-headings:font-black prose-headings:tracking-tighter',
                'prose-h1:text-4xl md:prose-h1:text-5xl md:prose-h1:mb-8 md:prose-h1:px-0',
                'prose-a:text-primary',
                'prose-img:rounded-2xl prose-img:shadow-2xl',
                'prose-headings:scroll-mt-24',
                'md:px-12 pb-12',
                location.pathname === '/componentes' && 'px-0! pb-0!'
              )}
            >
              {props.children}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default ComponentLayout
